import {ComponentStore} from '@ngrx/component-store';
import {ReportChannelAffinityState} from './report-channel-affinity';
import {Injectable} from '@angular/core';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {ReportChannelAffinityService} from '../../services/report-channel-affinity.service';
import {EMPTY, Observable, of, Subscription, switchMap, tap, throwError, withLatestFrom} from 'rxjs';
import {dialogOpen} from '../dialog/dialog.actions';
import {
  ParametersDialogComponent
} from '../../../reports/channel-affinity-report/channel-affinity-report/parameters-dialog/parameters-dialog.component';
import {ReportStore} from './report.store';
import {AdReportParams, AdReportParamsChannelAffinity} from '../../interfaces/ad-reports';
import {ReportStoreState} from './report';
import {EChartsOption} from 'echarts';
import {Serializer, SerializerResponse} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {catchError, filter} from 'rxjs/operators';

@Injectable()
export class ReportChannelAffinityStore extends ComponentStore<ReportChannelAffinityState> {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly loading$: Observable<boolean> = this.select((state: ReportChannelAffinityState): boolean => state.loading);
  public readonly report$: Observable<Array<any>> = this.select((state: ReportChannelAffinityState): Array<any> => state.report);
  public readonly chartOption$: Observable<EChartsOption> = this.select((state: ReportChannelAffinityState): EChartsOption => state.chartOption);

  public readonly load: (params: AdReportParamsChannelAffinity) => void = this.effect((params$: Observable<AdReportParamsChannelAffinity>): Observable<SerializerResponse<any>> => params$.pipe(
    tap((params: AdReportParamsChannelAffinity): void => {
      this.patchState({
        keepSingleEvents: params.keep_single_events,
        afterEvents: params.after_events,
        beforeEvents: params.before_events,
        loading: true
      });
    }),
    withLatestFrom(this._reportStore.state$, this._dataset$),
    switchMap(([params, reportStoreState, dataset]: [AdReportParamsChannelAffinity, ReportStoreState, Serializer<Site>]): Observable<SerializerResponse<any>> => this._channelAffinityService.generate(
      dataset, reportStoreState.uuid, reportStoreState.token, params
    ).pipe(
      tap((report: SerializerResponse<any>): void => {
        this.patchState({
          report: report.data,
          chartOption: this._channelAffinityService.getChartOptions(report.data, params,  (report as any).metric_external_name),
          loading: false
        });
      }),
      catchError(() => {
        this.patchState({ loading: false });

        return of();
      })
    ))
  ));

  private readonly _reportParams$: Subscription = this.effect((): Observable<[Partial<AdReportParams>, ReportStoreState, ReportChannelAffinityState]> => this._reportStore.reportParamsSubmitted$.pipe(
    withLatestFrom(this._reportStore.state$, this.state$),
    filter(([params, reportStoreState, state]: [Partial<AdReportParams>, ReportStoreState, ReportChannelAffinityState]): boolean => reportStoreState.submitted),
    tap(([params, reportStoreState, state]: [Partial<AdReportParams>, ReportStoreState, ReportChannelAffinityState]): void => {
      this.load({
        keep_single_events: state.keepSingleEvents,
        after_events: state.afterEvents,
        before_events: state.beforeEvents,
        ...reportStoreState.requestParams,
        ...reportStoreState.reportParams
      } as AdReportParamsChannelAffinity);
    })
  ));

  public readonly settingsDialog: () => void = this.effect((params$: Observable<void>): Observable<[void, ReportChannelAffinityState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([_, state]: [void, ReportChannelAffinityState]): void => {
      this._store.dispatch(dialogOpen({
        component: ParametersDialogComponent,
        config: {
          width: '600px',
          height: 'auto'
        },
        data: {
          keep_single_events: state.keepSingleEvents,
          after_events: state.afterEvents,
          before_events: state.beforeEvents
        }
      }));
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _channelAffinityService: ReportChannelAffinityService
  ) {
    super({
      report: [],
      chartOption: {},
      keepSingleEvents: false,
      afterEvents: 1,
      beforeEvents: 1,
      loading: false
    });
  }

}
